import React, { useEffect, useState, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-routing-machine/dist/leaflet-routing-machine.css";
import "leaflet-routing-machine";
import { Card, Select, DatePicker, Space, Timeline, Statistic } from "antd";
import {
  ClockCircleOutlined,
  UserOutlined,
  LoadingOutlined,
  TabletFilled,
  HomeFilled,
} from "@ant-design/icons";
import { getDirectusClient } from "../../lib/directus";
import { readItems, readUsers } from "@directus/sdk";
import { useSelector } from "react-redux";
import moment from "moment";
import Layouts from "../../components/Layouts";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";

// Create custom icon functions for each type
const createCustomIcon = (type) => {
  let iconHtml = "";
  let bgColor = "";

  if (type === "inicio" || type === "start") {
    iconHtml = `<span class="anticon"><svg xmlns="http://www.w3.org/2000/svg"  width="24" height="24" viewBox="0 0 24 24">

  <circle cx="12" cy="12" r="12" fill="#4CAF50"/>
  
  <g transform="translate(5, 5)" fill="#4CAF50" stroke="white" strokeWidth="0.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" transform="scale(0.6)"/>
    <polyline points="9 22 9 12 15 12 15 22" transform="scale(0.6)"/>
  </g>
</svg></span>`;
    bgColor = "green";
  } else if (type === "cliente" || type === "client") {
    iconHtml = `<span class="anticon"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
  <!-- Blue circle background -->
  <circle cx="12" cy="12" r="12" fill="#3F8CFF"/>
  
  <!-- User icon in white -->
  <g transform="translate(5, 5)" fill="white">
    <path d="M16,15a2,2,0,0,1-2,2H5a2,2,0,0,1-2-2,6,6,0,0,1,6-6h1A6,6,0,0,1,16,15Zm-7-8A3.5,3.5,0,1,0,5.5,3.5,3.5,3.5,0,0,0,9,7Z" transform="scale(0.7)"/>
  </g>
</svg></span>`;
    bgColor = "blue";
  } else {
    iconHtml = `<span class="anticon"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
  <!-- Red circle background -->
  <circle cx="12" cy="12" r="12" fill="#FF3B30"/>
  
  <!-- Building icon in white -->
  <g transform="translate(6, 4.5)" stroke="white" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect width="12" height="15" x="0" y="0" rx="1.5" ry="1.5"/>
    <path d="M3.75 15v-3h4.5v3"/>
    <path d="M3 3h.01"/>
    <path d="M9 3h.01"/>
    <path d="M6 3h.01"/>
    <path d="M6 6h.01"/>
    <path d="M6 9h.01"/>
    <path d="M9 6h.01"/>
    <path d="M9 9h.01"/>
    <path d="M3 6h.01"/>
    <path d="M3 9h.01"/>
  </g>
</svg></span>`;
    bgColor = "red";
  }

  return L.divIcon({
    className: `marker-${type}`,
    html: `<div class="w-7 h-7 rounded-full flex items-center justify-center text-white bg-${bgColor}-500">${iconHtml}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
};

// Add map and routing control refs

const ROAD_ADJUSTMENT_FACTOR = 1.38; // Adjustment factor for real road conditions

// Add the Haversine distance calculation function
const calculateHaversineDistance = (lat1, lon1, lat2, lon2) => {
  // Earth's radius in kilometers
  const R = 6371;

  // Convert latitude and longitude from degrees to radians
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;

  // Haversine formula
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in kilometers

  // Apply road adjustment factor
  return distance * ROAD_ADJUSTMENT_FACTOR;
};

// Replace your existing calculateDistance function with this one
const calculateDistance = (point1, point2) => {
  return calculateHaversineDistance(point1[0], point1[1], point2[0], point2[1]);
};

// Add OpenRouteService configuration
const OPENROUTESERVICE_API_KEY = process.env.REACT_APP_OPENROUTESERVICE_API_KEY;
const OPENROUTESERVICE_URL = 'https://api.openrouteservice.org/v2/directions/driving-car';

const getRoute = async (coordinates) => {
  try {
    const response = await fetch(OPENROUTESERVICE_URL, {
      method: 'POST',
      headers: {
        'Authorization': OPENROUTESERVICE_API_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        coordinates: coordinates,
        format: 'geojson',
        geometry: true,
        instructions: true,
        preference: 'recommended',
        units: 'km'
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('API Response:', data);
    return data;
  } catch (error) {
    console.error('Error fetching route:', error);
    throw error;
  }
};

// Correct polyline6 decoder function
const decodePolyline6 = (encoded) => {
  let index = 0;
  const len = encoded.length;
  let lat = 0;
  let lng = 0;
  const coordinates = [];

  while (index < len) {
    let shift = 0;
    let result = 0;
    let byte;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    const dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lat += dlat;

    shift = 0;
    result = 0;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    const dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lng += dlng;

    // Fix: Make sure coordinates are in the correct format and scale
    const coord = [lng * 1e-5, lat * 1e-5]; // Try using 1e-5 instead of 1e-6
    coordinates.push(coord);
  }

  return coordinates;
};

export default function InteractiveMap() {
  // Agregar nuevos estados
  const [coordinadores, setCoordinadores] = useState([]);
  const [supervisores, setSupervisores] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [selectedCoordinador, setSelectedCoordinador] = useState(null);
  const [selectedSupervisor, setSupervisor] = useState(null);
  const [loading, setLoading] = useState(false);

  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [trackingData, setTrackingData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [routeInfo, setRouteInfo] = useState(null);
  const mapRef = useRef(null);
  const routingControlRef = useRef(null);

  const userBodega = useSelector((state) => state.userdata?.bodega?.id);
  const userRoleID = useSelector((state) => state.userdata?.role?.name);
  const userId = useSelector((state) => state.userdata?.id);
  const getCurrentCoordinador = (userId) => {
    const coord = coordinadores.find((c) => c.id === userId);
    return coord ? `${coord.first_name} ${coord.last_name}` : "";
  };
  const formatDuration = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    }
    return `${minutes}min`;
  };

  const updateRouteInfo = (processedData) => {
    if (!processedData || processedData.length === 0) {
      setRouteInfo(null);
      return;
    }

    const startPoint = processedData.find((p) => p.tipo === "inicio");
    const clientPoints = processedData.filter((p) => p.tipo === "cliente");
    const endPoint = processedData.find((p) => p.tipo === "fin");

    let totalDistance = 0;
    const minutesPerKm = 1.2;

    // Calculate distances between all points in order
    for (let i = 0; i < processedData.length - 1; i++) {
      const distance = calculateDistance(
        processedData[i].coords,
        processedData[i + 1].coords
      );
      totalDistance += distance;
    }

    const startTime = startPoint ? new Date(startPoint.date_created) : null;
    const endTime = endPoint ? new Date(endPoint.date_created) : null;

    // Calculate total duration based on distance and average speed
    const estimatedDuration = Math.round(totalDistance * minutesPerKm);

    // Use actual duration if available, otherwise use estimated
    const totalDuration =
      startTime && endTime
        ? Math.round((endTime - startTime) / (1000 * 60))
        : estimatedDuration;

    setRouteInfo({
      totalDistance: totalDistance.toFixed(2),
      totalDuration: formatDuration(totalDuration),
      startTime: startPoint?.date_created,
      // Enviar todos los tiempos de clientes en lugar de solo el primero
      clientTimes: clientPoints.map((point) => point.date_created),
      endTime: endPoint?.date_created,
    });
  };

  const parseCoordinates = (coordString) => {
    try {
      if (typeof coordString === "string") {
        const [lat, lng] = coordString
          .replace(/\s+/g, "")
          .split(",")
          .map(Number);
        if (!isNaN(lat) && !isNaN(lng)) {
          return [lat, lng];
        }
      }
      return null;
    } catch (error) {
      console.error("Error parsing coordinates:", error);
      return null;
    }
  };

  const initializeRouting = async (processedData) => {
    if (!mapRef.current || !processedData || processedData.length < 2) return;

    if (routingControlRef.current) {
      mapRef.current.removeControl(routingControlRef.current);
    }

    // Convert coordinates to [longitude, latitude] format for OpenRouteService
    const coordinates = processedData.map(point => [point.coords[1], point.coords[0]]);
    console.log('Coordinates being sent:', coordinates);
    
    try {
      const routeData = await getRoute(coordinates);
      console.log('Route data:', routeData);
      
      if (routeData && routeData.routes && routeData.routes.length > 0) {
        const route = routeData.routes[0];
        console.log('Route object:', route);
        
        if (route.geometry && route.geometry.coordinates) {
          // For GeoJSON format
          const routeCoordinates = route.geometry.coordinates.map(coord => [coord[1], coord[0]]);
          console.log('Route coordinates (GeoJSON):', routeCoordinates);
          
          // Ensure the map is ready
          if (!mapRef.current) {
            console.error('Map reference is not available');
            return;
          }

          console.log('Creating polyline with GeoJSON coordinates');
          // Create a new polyline with the route coordinates
          const polyline = L.polyline(
            routeCoordinates,
            {
              color: '#1890ff',
              weight: 4,
              opacity: 0.7
            }
          );

          console.log('Adding polyline to map');
          // Add the polyline to the map
          polyline.addTo(mapRef.current);
          console.log('Polyline added to map');

          // Add markers for each point
          processedData.forEach((point, index) => {
            const marker = L.marker([point.coords[0], point.coords[1]], {
              icon: createCustomIcon(point.tipo)
            });
            marker.addTo(mapRef.current);
          });

          // Fit the map bounds to include all markers and the route
          const routeBounds = polyline.getBounds();
          const markerBounds = L.latLngBounds(processedData.map(point => [point.coords[0], point.coords[1]]));
          const bounds = routeBounds.extend(markerBounds);
          
          mapRef.current.fitBounds(bounds, {
            padding: [50, 50]
          });

          // Store the polyline reference for later removal if needed
          routingControlRef.current = polyline;
        } else if (route.geometry) {
          // For polyline6 format
          console.log('Route geometry (polyline6):', route.geometry);
          const decodedCoordinates = decodePolyline6(route.geometry);
          console.log('Decoded coordinates (polyline6):', decodedCoordinates);
          
          // Check if decoded coordinates are valid
          if (!decodedCoordinates || decodedCoordinates.length === 0) {
            console.error('No valid coordinates decoded from polyline');
            return;
          }
          
          // Ensure the map is ready
          if (!mapRef.current) {
            console.error('Map reference is not available');
            return;
          }

          console.log('Creating polyline with polyline6 coordinates');
          // Create a new polyline with the decoded coordinates
          const polylineCoords = decodedCoordinates.map(coord => [coord[1], coord[0]]);
          console.log('Polyline coordinates after transformation:', polylineCoords);
          
          const polyline = L.polyline(
            polylineCoords,
            {
              color: '#1890ff',
              weight: 4,
              opacity: 0.7
            }
          );

          console.log('Adding polyline to map');
          // Add the polyline to the map
          polyline.addTo(mapRef.current);
          console.log('Polyline added to map');

          // Add markers for each point
          processedData.forEach((point, index) => {
            const marker = L.marker([point.coords[0], point.coords[1]], {
              icon: createCustomIcon(point.tipo)
            });
            marker.addTo(mapRef.current);
          });

          try {
            // Fit the map bounds to include all markers and the route
            const routeBounds = polyline.getBounds();
            console.log('Route bounds:', routeBounds);
            
            const markerBounds = L.latLngBounds(processedData.map(point => [point.coords[0], point.coords[1]]));
            console.log('Marker bounds:', markerBounds);
            
            const bounds = routeBounds.extend(markerBounds);
            console.log('Combined bounds:', bounds);
            
            mapRef.current.fitBounds(bounds, {
              padding: [50, 50]
            });
          } catch (boundError) {
            console.error('Error setting bounds:', boundError);
            // Fallback to just fit to markers
            const markerBounds = L.latLngBounds(processedData.map(point => [point.coords[0], point.coords[1]]));
            mapRef.current.fitBounds(markerBounds, {
              padding: [50, 50]
            });
          }

          // Store the polyline reference for later removal if needed
          routingControlRef.current = polyline;
        } else {
          console.error('No geometry found in route data');
        }
      } else {
        console.error('No routes found in response');
      }
    } catch (error) {
      console.error('Error initializing routing:', error);
    }
  };

  // Add this function after initializeRouting
  const createSimpleRoute = (processedData) => {
    if (!mapRef.current || processedData.length < 2) return;
    
    console.log('Creating simple direct route between points');
    
    // If we have a previous route, remove it
    if (routingControlRef.current) {
      mapRef.current.removeLayer(routingControlRef.current);
    }
    
    // Create a simple polyline connecting all the points directly
    const coordinates = processedData.map(point => [point.coords[0], point.coords[1]]);
    console.log('Simple route coordinates:', coordinates);
    
    const polyline = L.polyline(coordinates, {
      color: '#FF5500',
      weight: 3,
      opacity: 0.7,
      dashArray: '5, 5'  // Make it dashed to differentiate from the API route
    });
    
    polyline.addTo(mapRef.current);
    console.log('Simple route added to map');
    
    // Add markers
    processedData.forEach((point, index) => {
      const marker = L.marker([point.coords[0], point.coords[1]], {
        icon: createCustomIcon(point.tipo)
      });
      marker.addTo(mapRef.current);
    });
    
    // Fit bounds
    const bounds = polyline.getBounds();
    mapRef.current.fitBounds(bounds, {
      padding: [50, 50]
    });
    
    // Store reference
    routingControlRef.current = polyline;
  };

  // Update getTrackingData to use the simple route as a fallback
  const getTrackingData = async () => {
    if (!selectedVendor) return;

    try {
      const directus = await getDirectusClient();
      const data = await directus.request(
        readItems("recorrido_gestion", {
          fields: ["coordenadas", "tipo", "date_created", "user_created"],
          filter: {
            _and: [
              {
                user_created: { _eq: selectedVendor },
              },
              {
                date_created: {
                  _gte: selectedDate.startOf("day").toISOString(),
                  _lte: selectedDate.endOf("day").toISOString(),
                },
              },
            ],
          },
          sort: ["date_created"],
        })
      );

      const processedData = data
        .map((item) => ({
          ...item,
          coords: parseCoordinates(item.coordenadas),
        }))
        .filter((item) => item.coords);

      setTrackingData(processedData);
      updateRouteInfo(processedData);
      
      try {
        await initializeRouting(processedData);
      } catch (routeError) {
        console.error('Failed to initialize routing with API, using simple route as fallback:', routeError);
        createSimpleRoute(processedData);
      }
    } catch (error) {
      console.error("Error fetching tracking data:", error);
    }
  };

  // Función para obtener coordinadores
  const getCoordinadores = async () => {
    setLoading(true);
    try {
      const directus = await getDirectusClient();
      const response = await directus.request(
        readUsers({
          fields: ["id", "first_name", "last_name", "role.*"],
          filter: {
            role: {
              name: { _eq: "coordinador_ruta" },
            },
            ...(userRoleID !== "adminapp" && userRoleID !== "Administrator"
              ? {
                  bodega: {
                    id: { _eq: userBodega },
                  },
                }
              : {}),
          },
        })
      );
      setCoordinadores(response);
    } catch (error) {
      console.error("Error fetching coordinadores:", error);
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener supervisores por coordinador
  const getSupervisores = async (coordinadorId) => {
    setLoading(true);
    try {
      const directus = await getDirectusClient();
      const response = await directus.request(
        readUsers({
          fields: ["id", "first_name", "last_name", "role.*", "superior.*"],
          filter: {
            role: {
              name: { _eq: "supervisor_ruta" },
            },
            superior: { id: { _eq: coordinadorId } },
            ...(userRoleID !== "adminapp" && userRoleID !== "Administrator"
              ? {
                  bodega: {
                    id: { _eq: userBodega },
                  },
                }
              : {}),
          },
        })
      );
      setSupervisores(response);
      setSupervisor(null);
      setSelectedVendor(null);
    } catch (error) {
      console.error("Error fetching supervisores:", error);
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener vendedores por supervisor
  const getVendedores = async (supervisorId) => {
    setLoading(true);
    try {
      const directus = await getDirectusClient();
      const response = await directus.request(
        readUsers({
          fields: ["id", "first_name", "last_name", "role.*", "superior.*"],
          filter: {
            role: {
              name: { _eq: "vendedor rutas" },
            },
            superior: { id: { _eq: supervisorId } },
            ...(userRoleID !== "adminapp" && userRoleID !== "Administrator"
              ? {
                  bodega: {
                    id: { _eq: userBodega },
                  },
                }
              : {}),
          },
        })
      );
      setVendedores(response);
      setSelectedVendor(null);
    } catch (error) {
      console.error("Error fetching vendedores:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Si el usuario es coordinador, establecerlo automáticamente como seleccionado
    if (userRoleID === "coordinador_ruta") {
      setSelectedCoordinador(userId);
      getSupervisores(userId);
      getCoordinadores(userId);
    } else {
      getCoordinadores();
    }
  }, [userRoleID, userId]);

  useEffect(() => {
    if (selectedVendor) {
      getTrackingData();
    }
  }, [selectedVendor, selectedDate]);

  return (
    <Layouts>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Space>
          {userRoleID === "coordinador_ruta" ? (
            <Select
              style={{ width: 200 }}
              value={getCurrentCoordinador(userId)}
              disabled={true}
              loading={loading}
            >
              {coordinadores
                .filter((coord) => coord.id === userId)
                .map((coord) => (
                  <Select.Option key={coord.id} value={coord.first_name}>
                    {`${coord.first_name} ${coord.last_name}`}
                  </Select.Option>
                ))}
            </Select>
          ) : (
            <Select
              style={{ width: 200 }}
              placeholder="Seleccionar Coordinador"
              value={selectedCoordinador}
              onChange={(value) => {
                setSelectedCoordinador(value);
                getSupervisores(value);
              }}
              loading={loading}
            >
              {coordinadores.map((coord) => (
                <Select.Option key={coord.id} value={coord.id}>
                  {`${coord.first_name} ${coord.last_name}`}
                </Select.Option>
              ))}
            </Select>
          )}

          <Select
            style={{ width: 200 }}
            placeholder="Seleccionar Supervisor"
            value={selectedSupervisor}
            onChange={(value) => {
              setSupervisor(value);
              getVendedores(value);
            }}
            disabled={!selectedCoordinador}
            loading={loading}
          >
            {supervisores.map((sup) => (
              <Select.Option key={sup.id} value={sup.id}>
                {`${sup.first_name} ${sup.last_name}`}
              </Select.Option>
            ))}
          </Select>

          <Select
            style={{ width: 200 }}
            placeholder="Seleccionar Vendedor"
            value={selectedVendor}
            onChange={setSelectedVendor}
            disabled={!selectedSupervisor}
            loading={loading}
          >
            {vendedores.map((vendor) => (
              <Select.Option key={vendor.id} value={vendor.id}>
                {`${vendor.first_name} ${vendor.last_name}`}
              </Select.Option>
            ))}
          </Select>

          <DatePicker
            onChange={(date) => setSelectedDate(date)}
            format="DD/MM/YYYY"
          />
        </Space>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr",
            gap: "16px",
          }}
        >
          <div>
            <MapContainer
              ref={mapRef}
              center={trackingData[0]?.coords || [4.6097, -74.0817]}
              zoom={13}
              style={{
                height: "500px",
                width: "100%",
                borderRadius: "8px",
                border: "1px solid #f0f0f0",
              }}
              dragging={true}
              touchZoom={false}
              scrollWheelZoom={true}
              doubleClickZoom={false}
              boxZoom={true}
              keyboard={false}
              zoomControl={true}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />

              {trackingData.map((track, index) => (
                <Marker
                  key={index}
                  position={track.coords}
                  icon={createCustomIcon(track.tipo)}
                >
                  <Popup>
                    <div style={{ minWidth: "200px" }}>
                      <div
                        style={{
                          fontWeight: "bold",
                          color:
                            track.tipo === "inicio"
                              ? "green"
                              : track.tipo === "fin"
                              ? "red"
                              : "blue",
                          marginBottom: "8px",
                        }}
                      >
                        {track.tipo.toUpperCase()}
                      </div>
                      <div style={{ marginBottom: "4px" }}>
                        <strong>Coordenadas:</strong>
                      </div>
                      <div>Latitud: {track.coords[0].toFixed(6)}</div>
                      <div>Longitud: {track.coords[1].toFixed(6)}</div>
                      <div style={{ marginTop: "8px" }}>
                        <strong>Hora:</strong>{" "}
                        {moment(track.date_created).format("HH:mm:ss")}
                      </div>
                      <div>
                        <strong>Fecha:</strong>{" "}
                        {moment(track.date_created).format("DD/MM/YYYY")}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          <Card
            title={
              <Space>
                <ClockCircleOutlined />
                Línea de Tiempo
              </Space>
            }
          >
            {routeInfo ? (
              <Space
                direction="vertical"
                size="large"
                style={{ width: "100%" }}
              >
                <Space
                  style={{ width: "100%", justifyContent: "space-between" }}
                >
                  {routeInfo && (
                    <>
                      <Statistic
                        title="Distancia total"
                        value={routeInfo.totalDistance}
                        suffix="km"
                      />
                      <Statistic
                        title="Tiempo total"
                        value={routeInfo.totalDuration}
                      />
                    </>
                  )}
                </Space>

                <Timeline>
                  {routeInfo?.startTime && (
                    <Timeline.Item dot={<HomeFilled />} color="green">
                      <div>Inicio</div>
                      <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                        {moment(routeInfo.startTime).format("HH:mm")}
                      </div>
                    </Timeline.Item>
                  )}

                  {routeInfo?.clientTimes &&
                    routeInfo.clientTimes.map((clientTime, index) => (
                      <Timeline.Item
                        key={index}
                        dot={<UserOutlined />}
                        color="#3F8CFF"
                      >
                        <div>Cliente</div>
                        <div style={{ fontSize: "12px", color: " #8c8c8c" }}>
                          {moment(clientTime).format("HH:mm")}
                        </div>
                      </Timeline.Item>
                    ))}

                  {routeInfo?.endTime && (
                    <Timeline.Item dot={<TabletFilled />} color="red">
                      <div>Destino final</div>
                      <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                        {moment(routeInfo.endTime).format("HH:mm")}
                      </div>
                    </Timeline.Item>
                  )}
                </Timeline>
              </Space>
            ) : (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <LoadingOutlined style={{ fontSize: 24, marginBottom: 16 }} />
                <div>Selecciona un vendedor y fecha para ver la ruta</div>
              </div>
            )}
          </Card>
        </div>
      </Space>
    </Layouts>
  );
}
